package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/modifier")
@Composable
fun ModifierComposePage() {
    BlogLayout(
        PageProperties(
            "Modifiers",
            ""
        ),
        BlogProperties("Jetpack Compose", "Modifiers")
    ) {

        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Modifiers Optimization",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em, bottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // Before: A typical "just make it work" modifier chain
                    // Before: "works", but heavier than it looks
                    Modifier
                        .clickable { onMessageClick() }                  // hit-test too early
                        .padding(horizontal = 12.dp, vertical = 6.dp)
                        .background(
                            color = MaterialTheme.colorScheme.surfaceVariant,
                            shape = RoundedCornerShape(12.dp)
                        )
                        .fillMaxWidth()
                        .padding(8.dp)                                   // extra layout work
                        .alpha(0.98f)                                    // layer A
                        .clip(RoundedCornerShape(12.dp))                 // layer B + path work
                        .shadow(4.dp, RoundedCornerShape(12.dp))         // layer C (often)
                        
                    // After: same visual, fewer layers, bounded ripple
                    Modifier
                        .fillMaxWidth()                                  // layout first
                        .padding(12.dp)
                        .graphicsLayer {                                 // consolidate heavy ops
                            alpha = 0.98f
                            shape = RoundedCornerShape(12.dp)
                            clip = true
                            shadowElevation = 4.dp.toPx()
                        }
                        .background(MaterialTheme.colorScheme.surfaceVariant) // drawn inside layer & clipped
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = rememberRipple(bounded = true)
                        ) { onMessageClick() }   
                    """.trimIndent()
            )

            //https://github.com/codewithdipesh/TicketBooking.git
            KotlinCode(
                code = """
                    //Counter Text Animation 
                    
                    @Composable
                    fun AnimatedNumber(
                        value: Int,
                        modifier: Modifier = Modifier
                    ) {
                        //previous value to determine scroll direction
                        var previousValue by remember { mutableIntStateOf(value) }
                        val scrollUp = value > previousValue   // going up → new number scrolls in from below

                        val offsetY = remember { Animatable(0f) }

                        LaunchedEffect(value) {
                            if (value != previousValue) {
                                offsetY.snapTo(if (scrollUp) 52f else -52f)
                                offsetY.animateTo(
                                    targetValue = 0f,
                                    animationSpec = tween(
                                        durationMillis = 220,
                                        easing = FastOutSlowInEasing
                                    )
                                )
                                previousValue = value
                            }
                        }

                        Box(
                            modifier = modifier.height(52.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            // Outgoing number =
                            Text(
                                text = previousValue.toString(),
                                color = Color.White,
                                fontSize = 28.sp,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier
                                    .graphicsLayer {
                                        translationY = if (scrollUp) offsetY.value - 52f else offsetY.value + 52f
                                        alpha = 1f - (kotlin.math.abs(
                                            if (scrollUp) offsetY.value - 52f else offsetY.value + 52f
                                        ) / 52f).coerceIn(0f, 1f)
                                    }
                            )
                            // Incoming number (scrolls in)
                            Text(
                                text = value.toString(),
                                color = Color.White,
                                fontSize = 28.sp,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier
                                    .graphicsLayer {
                                        translationY = offsetY.value
                                        alpha = 1f - (kotlin.math.abs(offsetY.value) / 52f).coerceIn(0f, 1f)
                                    }
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "WindowInsets",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Troves-eCommerce/Troves.git
            KotlinCode(
                code = """
                    val bannerVisible = banner != ConnectivityBanner.Hidden
                    Column(Modifier.fillMaxSize()) {
                        OfflineBanner(banner)
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                // While the banner occupies the status-bar area, tell the
                                // content below that the inset is already consumed so
                                // screens' own statusBarsPadding() don't double-pad.
                                .then(
                                    if (bannerVisible) Modifier.consumeWindowInsets(WindowInsets.statusBars)
                                    else Modifier
                                )
                        ) {
                            AppNav()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Column, Row and Box Arrangement",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Arrangement.Start: places items in the main axis start.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Arrangement.End: places items in the main axis end.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Arrangement.Center: places items in the main axis center.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Arrangement.SpaceBetween: places first item in the main axis start and the last item in the main axis end, then distribute the remaining items evenly in the space left.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Arrangement.SpaceAround: puts the same amount of space in the left and the right of each item (in a Row, or top and bottom in a Column). Notice that spaces in the middle will be twice as large as spaces in the ends.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Arrangement.SpaceEvenly: puts the same amount of space between items, the start and the end. It differentiates from space around because the spacing in start and end is the same as the spacing in the middle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.ARRANGEMENT_COLUMN,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Arrangement Column"
            )

            WebImage(
                src = Resources.Images.ARRANGEMENT_ROW,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Arrangement Row"
            )

            WebImage(
                src = Resources.Images.ARRANGEMENT_BOX,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Arrangement Box"
            )

            SpanText(
                text = "LayoutModifier",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A LayoutModifier in Jetpack Compose is a special kind of Modifier that can participate in the layout phase. Unlike draw or pointer modifiers, a LayoutModifier can measure and place its child, letting you change constraints, read the child’s measured size, and decide where the child is positioned. It’s useful for behaviors like enforcing aspect ratios, custom padding that depends on size, or aligning/offsetting content based on measurements. You create one by implementing the LayoutModifier interface (or using Modifier.layout) and overriding measure to return a custom MeasureResult with placeable placements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Modifiers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifiers as decorators for your UI components. They allow you to enhance and customize your components in various ways, from simple visual changes to complex behavioral modifications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifiers are essential for adding properties like padding, size, background, click actions, and layout adjustments to composable elements without altering the composable function itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifiers are stateless, chainable and reusable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun MyComponent(
                        modifier: Modifier = Modifier  //1. Accept it
                    ) { }
                    
                    @Composable
                    fun MyComponent(
                        Box (modifier = modifier) { //2. Apply it to the root
                            // content
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Avoid this for frequent changes
                    .background(animatedColor)
                    
                    //Do this instead
                    .drawBehind {
                        drawRect(animatedColor)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //When you need natural scrolling in both directions (dashboards, maps, spreadsheets), avoid nesting horizontalScroll() and verticalScroll().
                    
                    Box(
                        modifier = Modifier.scrollable2D(
                            state = rememberScrollable2DState { delta -> delta }
                        )
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     @Composable
                     fun CornerDiagonalRibbon(
                         text: String,
                         modifier: Modifier = Modifier,
                         ribbonColor: Color = Color.Red,
                         textColor: Color = Color.White
                     ) {
                         Box(modifier = modifier) {
                             Box(
                                 modifier = Modifier
                                     .size(100.dp)
                                     .align(Alignment.TopEnd)
                                     .drawBehind {
                                         val path = Path().apply {
                                             moveTo(size.width, 0f)
                                             lineTo(size.width, size.height)
                                             lineTo(0f, 0f)
                                             close()
                                         }
                                         drawPath(path, ribbonColor)
                                     }
                             )

                             Text(
                                 text = text,
                                 color = textColor,
                                 fontWeight = FontWeight.Bold,
                                 modifier = Modifier
                                     .align(Alignment.TopEnd)
                                     .padding(top = 12.dp, end = 4.dp)
                                     .rotate(45f)
                             )
                         }
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Rotate Vertically
                    
                    private fun Modifier.rotateVertically(): Modifier {
                        // Rotate the content
                        // The rotate modifier applies a 270° rotation, affecting only how the text is drawn.
                        val rotate = Modifier.rotate(270f)
                        val adjustBounds = layout { measurable, constraints ->
                            val placeable = measurable.measure(
                                // Re-measure with relaxed constraints
                                // The custom layout block measures the child with unbounded width and height. 
                                // This prevents clipping and lets the text report its natural size after rotation.
                                constraints.copy(
                                    minWidth = 0,
                                    minHeight = 0,
                                    maxWidth = Constraints.Infinity,
                                    maxHeight = Constraints.Infinity
                                )
                            )
                            // Swap width and height to match the rotated orientation and the child is re-centered.
                            layout(placeable.height, placeable.width) {
                                // Re-center the rotated content so it stays aligned within the new bounds.
                                placeable.place(
                                    x = -(placeable.width / 2 - placeable.height / 2),
                                    y = -(placeable.height / 2 - placeable.width / 2)
                                )
                            }
                        }
                        return this
                            .then(rotate)
                            .then(adjustBounds)
                    }
                    """.trimIndent()
            )

            //https://github.com/AliBehrouzinia/Task-Manager
            KotlinCode(
                code = """
                    import androidx.compose.ui.Modifier

                    inline fun Modifier.applyIf(condition : Boolean, modifier : Modifier.() -> Modifier) : Modifier {
                        return if (condition) {
                            then(modifier(Modifier))
                        } else {
                            this
                        }
                    }
                    """.trimIndent()
            )

            //kt.academy
            KotlinCode(
                code = """
                    @OptIn(ExperimentalContracts::class, ExperimentalExtendedContracts:: class)
                    fun <T: R, R> T.letIf(condition: Boolean, block: (T) -> R): R {
                        contract {
                            callsInPlace(block, InvocationKind.AT_MOST_ONCE)
                            condition.holdsIn(block)
                        ｝
                        return if (condition) ‹
                            block(this)
                        } else {
                            this
                        ｝
                    ｝
                    
                    val newFlow: Flow<Post> = postsFlow
                    •letIf(requiredTags != null) { it.filter { it.tags.containsAll(requiredTags) } }
                    .letIf(showOnlyImportant） ｛ it.filter ｛ it.important ｝ ｝
                    
                    // Compose
                    val modifier = Modifier
                        •letIf(onClick!= null) { it.clickable(onClick = onclick) }
                        •letIf(isError) { it.background(Color(0xFFFFEBEE)) }
                        •letIf(selected) { it.border(1.dp, Color(0xFF64B5F6)) }
                    """.trimIndent()
            )

            SpanText(
                text = "When We Need to Make the Modifier Factory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier logic is reused.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Styling must stay consistent.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier chain becomes long.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Logic involves conditions",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We want readable composables",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Modifier.graphicsLayer()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/percy-g2/kmp_utxo.git (Crypto App)
            KotlinCode(
                code = """
                        // --- Bounce animation magic on first appearance! ---
                        val appeared = remember(tickerData.symbol) { mutableStateOf(false) }
                        LaunchedEffect(tickerData.symbol) {
                            appeared.value = true
                        }
                        val bounceScale by animateFloatAsState(
                            targetValue = if (appeared.value) 1f else 0.95f,
                            animationSpec = spring(
                                stiffness = Spring.StiffnessMedium,
                                dampingRatio = 0.42f // subtle bounce
                            ),
                            label = "TickerCardBounceScale"
                        )
                        // --- End bounce animation ---
                        
                        Box( 
                            modifier = Modifier
                            .fillMaxWidth()
                            .graphicsLayer(
                                scaleX = bounceScale,
                                scaleY = bounceScale
                            )
                        ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Banner View / Corner Ribbon  / Diagonal Ribbon 
                    
                    @Composable
                    fun TagView() {
                        Text(
                            modifier = Modifier
                                .width(800.dp)
                                .graphicsLayer(
                                    translationX = 400f,
                                    translationY = 220f,
                                    rotationZ = 40f
                                )
                                .background(AppTheme.colors.splashscreenBanner)
                                .padding(vertical = 8.dp),
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onError,
                            text = stringResource(Res.string.splashscreen_subTitle),
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        /** Adds a gradient overlay on the composable (gradient default if null) */
                        fun Modifier.gradientOverlay(colors: List<Color>? = null): Modifier = composed {
                            return@composed this
                                .graphicsLayer(alpha = 0.99f)
                                .drawWithCache {
                                    onDrawWithContent {
                                        drawContent()
                                        drawRect(
                                            brush = Brush.linearGradient(
                                                colors = colors!!
                                            ), blendMode = BlendMode.SrcAtop
                                        )
                                    }
                                }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Modifier.rubberbandClickable(onClick: () -> Unit): Modifier {
                      var pressed by remember { mutableStateOf(false) }

                      val scale by
                        animateFloatAsState(
                          targetValue = if (pressed) 0.9f else 1f, // Shrink slightly when pressed
                          animationSpec = spring(stiffness = 500f),
                          label = "", // Control the "rubberband" effect
                        )

                      return this.graphicsLayer(scaleX = scale, scaleY = scale) // Apply scaling
                        .clickable(
                          onClick = {
                            pressed = true
                            onClick()
                          },
                          onClickLabel = null,
                        )
                        .thenIf(!pressed, ifTrue = { this }) { this } // Reset `pressed` to false after animation
                    }
                    
                    //bold
                    @Composable
                    fun Modifier.bold(isBold: Boolean) =
                      this.thenIf(isBold, ifTrue = { graphicsLayer(scaleX = 1.2f, scaleY = 1.2f) }, ifFalse = { this })
                    """.trimIndent()
            )

            SpanText(
                text = "Modifier.selectableGroup()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/stoyan-vuchev/magic-message.git (Bottom Bar Hide/Show Animation, Setting List Good Animation)
            //Optimization tips
            KotlinCode(
                code = """
                    //Use this modifier to group a list of selectable items like Tabs or RadioButtons together for accessibility purpose.
                    
                    Modifier.selectableGroup()
                    """.trimIndent()
            )

            SpanText(
                text = "Modifier Condition (if, them. ifNotNull, true/false, FocusTab)",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalContracts:: class, ExperimentalExtendedContracts:: class)
                    fun <T: R, R> T.LetIf(condition: Boolean, block: (T) -> R: R {
                        contract {
                            callsInPlace(block, InvocationKind.AT_MOST_ONCE) 
                            condition.holdsIn(block)
                        ｝
                        return if (condition) {
                            block(this)
                        } else {
                            this
                        ｝
                    ｝
                    
                    // FLoW
                    val newFLow: Flow<Post> = postsFlow
                    •letIf(requiredTags != null) { it.filter { it.tags.containsAll(requiredTags)} }
                    .letIf(showOnlyImportant) { it.filter { it.important} }
                    
                    val modifier = Modifier
                    .letIf(onClick != null) { it.clickable(onClick = onclick) }
                    .letIf(isError) { it.background(Color(0xFFFFEBEE)) }
                    .letIf(selected) {it.border1.dp, Color(0x64556)) }
                    """.trimIndent()
            )

            //https://github.com/Narsuf/Stonks.git
            KotlinCode(
                code = """
                    //Modifier if condition
                    fun <T> Modifier.ifNotNull(value: T?, block: Modifier.(T) -> Modifier): Modifier {
                        return if (value != null) this.block(value) else this
                    }
                    
                    @Composable
                    fun PrimaryButton(
                        title: String,
                        icon: ImageVector? = null,
                        modifier: Modifier = Modifier,
                        onClick: () -> Unit,
                    ) {
                        Button(
                            onClick = onClick,
                            modifier = modifier,
                        ) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(Spacing.smaller),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                icon?.let {
                                    Icon(
                                        imageVector = icon,
                                        contentDescription = "",
                                        modifier = Modifier.size(20.dp)
                                    )
                                }
                                Text(
                                    text = title,
                                    modifier = Modifier.ifNotNull(icon) { padding(end = Spacing.smallest) },
                                    color = MaterialTheme.colorScheme.onPrimary,
                                    style = MaterialTheme.typography.bodyMedium,
                                )
                            }
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    Modifier.background(Color.Red).fillMaxWidth(fraction = 0.8f)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //modifier then condition
                    
                    Image(
                        painter = painterResource(id = R.drawable.ic_avatar),
                        contentDescription = "Profile Picture"
                        modifier = Modifier
                            .size size(100.dp)
                            .clip (CircleShape)
                            
                            //Keep the chain flowing!
                            .then(
                                if (condition) Modifier.border(2.dp, Color. Gold, CircleShape)
                                else Modifier
                            )
                        )        
                    """.trimIndent()
            )

            //https://github.com/SkyD666/Compone.git
            //https://shivangicodes.medium.com
            KotlinCode(
                code = """
                    //Modifier if condition
                     
                    import androidx.compose.ui.Modifier
                    import kotlin.contracts.InvocationKind
                    import kotlin.contracts.contract

                    inline fun Modifier.thenIf(condition: Boolean, block: Modifier.() -> Modifier): Modifier {
                        contract {
                            callsInPlace(block, InvocationKind.AT_MOST_ONCE)
                        }
                        return if (condition) block() else this
                    }

                    inline fun <T> Modifier.thenIfNotNull(obj: T?, block: Modifier.(T) -> Modifier): Modifier {
                        contract {
                            callsInPlace(block, InvocationKind.AT_MOST_ONCE)
                        }
                        return if (obj != null) block(obj) else this
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Condition

                    //1
                    fun Modifier.conditional(
                        condition: Boolean,
                        ifTrue: Modifier.() -> Modifier,
                        ifFalse: (Modifier.() -> Modifier)? = null
                    ): Modifier {
                        return if (condition) {
                            then(ifTrue(Modifier))
                        } else if (ifFalse != null) {
                            then(ifFalse(Modifier))
                        } else {
                            this
                        }
                    }

                    //2
                    @Composable
                    inline fun <V> Modifier.modifyIfNotNull(
                        value: V?,
                        block: @Composable Modifier.(V) -> Modifier
                    ): Modifier {
                        if (value != null) return this.block(value)
                        return this
                    }

                    //3
                    @Composable
                    inline fun Modifier.modifyIfTrue(value: Boolean, block: Modifier.() -> Modifier) = when {
                        value -> this.block()
                        else -> this
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Modifier.clickableWithoutHoverEffect(onClick: () -> Unit) =
                        this.clickable(onClick = onClick, indication = null, interactionSource = remember { MutableInteractionSource() })
                    """.trimIndent()
            )


            KotlinCode(
                code = """
                    //changeFocusWithTab

                    @Composable
                    fun Modifier.changeFocusWithTab(
                        focusManager: FocusManager = LocalFocusManager.current
                    ) = this.onPreviewKeyEvent {
                        if (it.type == KeyEventType.KeyDown && it.key == Key.Tab) {
                            if (it.isShiftPressed) {
                                focusManager.moveFocus(FocusDirection.Previous)
                            } else {
                                focusManager.moveFocus(FocusDirection.Next)
                            }
                            true
                        } else {
                            false
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "composed",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The composed {} function was previously recommended for creating custom modifiers that rely on composition, but it is now discouraged due to performance concerns. Unlike a simple modifier factory, composed {} initializes and updates within the composition, leading to additional recompositions and overhead. Although this approach allows dynamic changes within the composition, it should be avoided in favor of Modifier.Node, which provides a more efficient alternative for stateful modifiers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                        /** Adds a gradient overlay on the composable (gradient default if null) */
                        fun Modifier.gradientOverlay(colors: List<Color>? = null): Modifier = composed {
                            return@composed this
                                .graphicsLayer(alpha = 0.99f)
                                .drawWithCache {
                                    onDrawWithContent {
                                        drawContent()
                                        drawRect(
                                            brush = Brush.linearGradient(
                                                colors = colors!!
                                            ), blendMode = BlendMode.SrcAtop
                                        )
                                    }
                                }
                        }
                    """.trimIndent()
            )

            //https://github.com/Grippo-app/grippo-mobile.git
            KotlinCode(
                code = """
                    @Composable
                    public fun Modifier.scalableClick(
                        enabled: Boolean = true,
                        onClick: () -> Unit
                    ): Modifier = composed {
                        if (!enabled) return@composed this

                        val interactionSource = remember { MutableInteractionSource() }
                        val scaleDown = 0.97f
                        var pressed by remember { mutableStateOf(false) }

                        LaunchedEffect(interactionSource) {
                            interactionSource.interactions.collect { interaction ->
                                when (interaction) {
                                    is PressInteraction.Press -> pressed = true
                                    is PressInteraction.Release, is PressInteraction.Cancel -> pressed = false
                                }
                            }
                        }

                        val scale by animateFloatAsState(
                            targetValue = if (pressed) scaleDown else 1f,
                            animationSpec = tween(durationMillis = 120, easing = LinearOutSlowInEasing),
                            label = "clickScale"
                        )

                        this
                            .scale(scale)
                            .clickable(
                                interactionSource = interactionSource,
                                indication = null,
                                onClick = onClick
                            )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun Modifier.customPadding(
                        horizontal: Dp = 16.dp,
                        vertical : Dp = 16.dp
                    ) = composed {
                        this.padding(horizontal = horizontal,vertical = vertical)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Modifier.Node",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier.Node is the preferred way to implement custom modifiers due to its efficiency and lifecycle management capabilities. It allows developers to define modifiers with lower-level control over layout, drawing, and interactions while maintaining performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Modifier.Node implementation consists of three components:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Modifier.Node - Defines the behavior of the modifier.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ModifierNodeElement - Provides a stateless factory to create or update the node.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Modifier Factory - Exposes the modifier to be used in the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    // Modifier Factory
                    fun Modifier.circle(color: Color) = this.then(CircleElement(color))

                    // ModifierNodeElement
                    private data class CircleElement(
                        val color: Color
                    ) : ModifierNodeElement<CircleNode>() {

                        override fun create(): CircleNode {
                            return CircleNode(color)
                        }

                        override fun update(node: CircleNode) {
                            node.color = color
                        }
                    }

                    // ModifierNode
                    private class CircleNode(
                        var color: Color
                    ) : ModifierNode(), DrawModifierNode {

                        override fun ContentDrawScope.draw() {
                            drawCircle(color = color) // draw circle
                            drawContent() // draw child content
                        }
                    }
                    
                    Box(
                        modifier = Modifier
                            .size(150.dp)
                            .circle(Color.Blue)
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Modifiers Parameter Types",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Component-Specific Parameters: These properties only make sense for a particular component type. For example, text, fontSize, color, maxLines, overflow, textAlign",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Universal Modifiers: These properties could apply to any component, such as width, height, padding, or background color. fillMaxWidth(), background(), padding(), clickable()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "State-Based Modifiers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedStateCard(
                        state: CardState,
                        modifier: Modifier = Modifier,
                        content: @Composable () -> Unit,
                    ) {
                        val transition = updateTransition(state, label = "CardState")

                        val elevation by transition.animateDp(label = "elevation") { state ->
                            when (state) {
                                CardState.Normal -> 2.dp
                                CardState.Elevated -> 8.dp
                                CardState.Pressed -> 4.dp
                            }
                        }

                        val scale by transition.animateFloat(label = "scale") { state ->
                            when (state) {
                                CardState.Normal -> 1f
                                CardState.Elevated -> 1.05f
                                CardState.Pressed -> 0.95f
                            }
                        }

                        Card(
                            modifier = modifier
                                .graphicsLayer {
                                    scaleX = scale
                                    scaleY = scale
                                }
                                .shadow(elevation)
                        ) {
                            content()
                        }
                    }

                    enum class CardState {
                        Normal, Elevated, Pressed
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Custom Layout Modifiers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun Modifier.aspectRatioWithMaxHeight(
                        ratio: Float,
                        maxHeight: Dp,
                    ) = layout { measurable, constraints ->
                        val maxHeightPx = maxHeight.roundToPx()
                        val constrainedHeight = constraints.maxHeight.coerceAtMost(maxHeightPx)
                        val width = (constrainedHeight * ratio).roundToInt()

                        val placeable = measurable.measure(
                            Constraints(
                                maxWidth = width,
                                maxHeight = constrainedHeight
                            )
                        )

                        layout(width, constrainedHeight) {
                            placeable.place(0, 0)
                        }
                    }

                    // Usage example
                    @Composable
                    fun ResponsiveImage(
                        imageResource: ImageResource,
                        contentDescription: String?,
                        modifier: Modifier = Modifier,
                    ) {
                        Image(
                            painter = painterResource(imageResource),
                            contentDescription = contentDescription,
                            modifier = modifier
                                .aspectRatioWithMaxHeight(
                                    ratio = 16f / 9f,
                                    maxHeight = 300.dp
                                )
                                .clip(RoundedCornerShape(8.dp))
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Vertical Scrolling",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberScrollState() — remembers how far you scrolled.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "verticalScroll() — makes your screen scroll up and down.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val scrollState = rememberScrollState()

                    Box(
                        modifier = Modifier
                        •fillMaxSize()
                        •verticalScroll(scrollState)
                    ) {}
                    """.trimIndent()
            )

            SpanText(
                text = "drawBehind",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The most common entry point. It allows you to draw behind the Composable’s main content. Custom backgrounds, shadows, or decorative flourishes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/youndie/mani.git
            KotlinCode(
                code = """
                    //DashBorder Rect Using PathEffect
                    
                    val outline = MaterialTheme.colorScheme.outline
                    val chipHeight = with(LocalDensity.current) { AssistChipDefaults.Height.toPx() }

                    AssistChip(
                        onClick = onCreateNew,
                        label = { Text("New") },
                        border = null,
                        modifier = Modifier.drawBehind {
                            val inset = ((size.height - chipHeight) / 2f).coerceAtLeast(0f)
                            drawRoundRect(
                                color = outline,
                                topLeft = Offset(0f, inset),
                                size = Size(size.width, size.height - inset * 2),
                                cornerRadius = CornerRadius(8.dp.toPx()),
                                style = Stroke(
                                    width = 1.dp.toPx(),
                                    pathEffect = PathEffect.dashPathEffect(floatArrayOf(6f, 6f))
                                )
                            )
                        },
                        leadingIcon = {
                            Icon(
                                Icons.Filled.Add,
                                contentDescription = "add",
                                Modifier.size(AssistChipDefaults.IconSize)
                            )
                        }
                    )
                    """.trimIndent()
            )

            //https://github.com/rajdeepvaghela/AboutMe.git
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun PortfolioScreen() {
                        val scope = rememberCoroutineScope()
                        val backgroundStripColor = PortfolioTheme.colors.accentStroke

                        val infiniteTransition = rememberInfiniteTransition(label = "bgAnimation")
                        val animAngle by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = (2 * PI).toFloat(),
                            animationSpec = infiniteRepeatable(
                                animation = tween(durationMillis = 12000, easing = LinearEasing),
                                repeatMode = RepeatMode.Restart
                            ),
                            label = "bgAngle"
                        )

                        val pagerState = rememberPagerState(initialPage = 0, pageCount = { Tabs.entries.size })

                        // Lazy cursor-following dot
                        var dotEnabled by remember { mutableStateOf(true) }
                        var cursorX by remember { mutableStateOf(-200f) }
                        var cursorY by remember { mutableStateOf(-200f) }
                        var dotX by remember { mutableStateOf(-200f) }
                        var dotY by remember { mutableStateOf(-200f) }

                        // Each display frame, ease the dot 8% of the way toward the cursor
                        LaunchedEffect(Unit) {
                            while (true) {
                                withFrameMillis {
                                    dotX += (cursorX - dotX) * 0.08f
                                    dotY += (cursorY - dotY) * 0.08f
                                }
                            }
                        }

                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(PortfolioTheme.colors.background)
                                .pointerInput(Unit) {
                                    awaitPointerEventScope {
                                        while (true) {
                                            val event = awaitPointerEvent(PointerEventPass.Main)
                                            event.changes.firstOrNull()?.position?.let { pos ->
                                                cursorX = pos.x
                                                cursorY = pos.y
                                            }
                                        }
                                    }
                                }
                                .drawBehind {
                                    val strokeWidth = 1.dp.toPx()
                                    
                                    // Use minDimension so circles stay proportional in both portrait & landscape
                                    val minDim = minOf(size.width, size.height)
                                    val driftX = minDim * 0.04f
                                    val driftY = minDim * 0.03f

                                    // Phase offsets so each circle animates independently (120° apart)
                                    val phase1 = animAngle
                                    val phase2 = animAngle + (2 * PI / 3).toFloat()
                                    val phase3 = animAngle + (4 * PI / 3).toFloat()

                                    // Circle 1 — top-right (center at right edge so visible in both orientations)
                                    drawCircle(
                                        color = backgroundStripColor.copy(alpha = 0.1f),
                                        radius = minDim * 0.8f * (1f + sin(phase1.toDouble()).toFloat() * 0.04f),
                                        center = Offset(
                                            size.width * 0.85f + cos(phase1.toDouble()).toFloat() * driftX,
                                            size.height * 0.1f + sin(phase1.toDouble()).toFloat() * driftY
                                        ),
                                        style = Stroke(width = strokeWidth)
                                    )
                                    
                                    // Circle 2 — mid-left (slightly off-screen left)
                                    drawCircle(
                                        color = backgroundStripColor.copy(alpha = 0.1f),
                                        radius = minDim * 0.9f * (1f + sin(phase2.toDouble()).toFloat() * 0.04f),
                                        center = Offset(
                                            -minDim * 0.1f + cos(phase2.toDouble()).toFloat() * driftX,
                                            size.height * 0.45f + sin(phase2.toDouble()).toFloat() * driftY
                                        ),
                                        style = Stroke(width = strokeWidth)
                                    )
                                    
                                    // Circle 3 — bottom-center
                                    drawCircle(
                                        color = backgroundStripColor.copy(alpha = 0.1f),
                                        radius = minDim * 0.7f * (1f + sin(phase3.toDouble()).toFloat() * 0.04f),
                                        center = Offset(
                                            size.width * 0.55f + cos(phase3.toDouble()).toFloat() * driftX,
                                            size.height * 0.9f + sin(phase3.toDouble()).toFloat() * driftY
                                        ),
                                        style = Stroke(width = strokeWidth)
                                    )
                                }
                        ) {
                            LaunchedEffect(pagerState.targetPage) {
                                when (pagerState.targetPage) {
                                    Tabs.Projects.ordinal -> {
                                        onEvent(PortfolioScreenEvent.TrackEvent(AppViewModel.Events.OpenProjects))
                                    }

                                    Tabs.AboutMe.ordinal -> {
                                        onEvent(PortfolioScreenEvent.TrackEvent(AppViewModel.Events.OpenAboutMe))
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Line Draw Up
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(50.dp)
                            .drawBehind {
                                drawLine(
                                    color = TextBlack,
                                    start = Offset(0f, 0f),
                                    end = Offset(size.width, 0f),
                                    strokeWidth = 1.dp.toPx()
                                )
                            }
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "drawWithContent",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is your “interceptor.” It gives you control over the rendering order. Overlays, “Glass” effects, or any case where you need to wrap the UI content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    Text(
                        "Text with semi-transparent overlay",
                        modifier = Modifier.drawWithContent { //ContentDrawScope receiver
                            drawContent() // Draw the text first
                            drawRect(Color.Red.copy(alpha = 0.2f)) // Draw a tint over it
                        }
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "drawWithCache",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is your choice for performance. Drawing often involves expensive object allocations (like Path or Shader). drawWithCache allows you to initialize these objects only when the size changes, or when read state values change, rather than on every frame.  Complex shapes or gradients that don’t change every frame.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun AnimatedPaymentItemSelection(
                        cards: List<String>,
                        selectedCardIndex: Int,
                        spacing: Dp = 6.dp,
                        onCardSelected: (Int) -> Unit,
                    ) {
                        val density = LocalDensity.current
                        var itemHeight by remember { mutableFloatStateOf(0F) }

                        val animatedSelectionStrokeOffset by animateFloatAsState(
                            targetValue = (density.run { spacing.toPx() } + itemHeight) * selectedCardIndex,
                            animationSpec = tween(1000),
                            label = "animatedSelectionStrokeOffset"
                        )

                        Column(
                            modifier = Modifier.drawWithCache {
                                val totalSpacing = (spacing.toPx()) * (cards.size - 1)
                                itemHeight = (size.height - totalSpacing) / cards.size

                                onDrawWithContent {
                                    drawContent()

                                    translate(top = animatedSelectionStrokeOffset) {
                                        drawRoundRect(
                                            color = Color.Black,
                                            size = Size(width = size.width, height = itemHeight),
                                            cornerRadius = CornerRadius(x = 8.dp.toPx(), y = 8.dp.toPx()),
                                            style = Stroke(width = 1.5.dp.toPx())
                                        )
                                    }
                                }
                            }
                        ) {
                            cards.forEachIndexed { index, cardNumber ->
                                PaymentItem(
                                    cardNumber = cardNumber,
                                    selected = index == selectedCardIndex,
                                    onClick = { onCardSelected(index) }
                                )
                                if (index != cards.lastIndex) {
                                    Spacer(modifier = Modifier.height(spacing))
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun PaymentItem(
                        cardNumber: String,
                        selected: Boolean,
                        onClick: () -> Unit,
                    ) {
                        val colorScheme = LocalColorScheme.current

                        val radioColor by animateColorAsState(
                            targetValue = if (selected) colorScheme.secondary else colorScheme.primaryContainer
                        )
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(8.dp))
                                .clickable(interactionSource = null, indication = rememberRipple(), onClick = onClick)
                                .background(color = colorScheme.primaryContainer)
                                .padding(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(20.dp)
                                    .border(width = 2.dp, color = colorScheme.secondary, shape = CircleShape)
                                    .padding(4.dp)
                                    .background(color = radioColor, shape = CircleShape)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "•••••••• $cardNumber",
                                style = TextStyle(
                                    color = colorScheme.secondary,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 14.sp,
                                    fontFamily = SoraFontFamily,
                                    letterSpacing = 0.5.sp
                                )
                            )
                            Spacer(modifier = Modifier.weight(1F))
                            Text(
                                text = "VISA",
                                style = TextStyle(
                                    color = colorScheme.secondary,
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 14.sp,
                                    fontFamily = SoraFontFamily
                                )
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "𝑾𝒉𝒂𝒕 𝑫𝒐𝒆𝒔 𝒐𝒏𝑮𝒍𝒐𝒃𝒂𝒍𝒍𝒚𝑷𝒐𝒔𝒊𝒕𝒊𝒐𝒏𝒆𝒅 𝑫𝒐? (How to get exact size without recomposition?)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you apply the onGloballyPositioned modifier to a composable, it provides a callback with LayoutCoordinates. This object contains information about the size and position of the composable relative to the entire screen. You can use this information to adjust your UI dynamically based on the position of other elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = $$"""
                    @Composable
                    fun GetSizeExample() {
                        var size by remember { mutableStateOf<IntSize?>(null) }

                        Box(
                            modifier = Modifier
                                .size(100.dp)
                                .onGloballyPositioned { layoutCoordinates ->
                                    size = layoutCoordinates.size
                                }
                                .background(Color.Blue)
                        )

                        Text(text = "Size: ${size?.width} x ${size?.height}")
                    }
                    """.trimIndent()
                )
            }

            //https://github.com/rainxchzed/JanuaryMiniChallenges.git
            KotlinCode(
                code = """
                    @Composable
                    private fun NewYearTheme(
                        state: SettingsState,
                        uiState: UiState,
                        onAction: (SettingsAction) -> Unit
                    ) {
                        Card(
                            colors = CardDefaults.cardColors(
                                containerColor = SettingsColors.surface
                            )
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(12.dp)
                                    .animateContentSize(),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Text(
                                    text = "New Year Theme",
                                    fontFamily = plusJakartaSansFont(),
                                    fontWeight = FontWeight.Medium,
                                    fontSize = 14.sp,
                                    color = SettingsColors.textSecondary
                                )

                                var containerWidth by remember {
                                    mutableStateOf(0.dp)
                                }
                                val density = LocalDensity.current

                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .onGloballyPositioned {
                                            containerWidth = with(density) {
                                                it.size.width.toDp()
                                            }
                                        }
                                        .clip(RoundedCornerShape(10.dp))
                                        .then(
                                            if (uiState.isSelectThemeDropdownExpanded) {
                                                Modifier.border(
                                                    width = 2.dp,
                                                    color = SettingsColors.primary,
                                                    shape = RoundedCornerShape(10.dp)
                                                )
                                            } else Modifier
                                        )
                                        .clickable(onClick = {
                                            onAction(SettingsAction.OnToggleThemeDropdown)
                                        })
                                        .background(SettingsColors.surfaceInput)
                                        .padding(horizontal = 14.dp, vertical = 12.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text(
                                        text = state.selectedTheme.label(),
                                        fontFamily = plusJakartaSansFont(),
                                        fontWeight = FontWeight.Medium,
                                        fontSize = 18.sp,
                                        color = SettingsColors.textPrimary
                                    )

                                    Icon(
                                        imageVector = if (uiState.isSelectThemeDropdownExpanded) {
                                            Icons.Default.KeyboardArrowUp
                                        } else Icons.Default.KeyboardArrowDown,
                                        contentDescription = null,
                                        modifier = Modifier.size(16.dp),
                                        tint = SettingsColors.textPrimary
                                    )
                                }

                                ThemeDropdown(
                                    isExpanded = uiState.isSelectThemeDropdownExpanded,
                                    selectedTheme = state.selectedTheme,
                                    onDismissRequest = {
                                        onAction(SettingsAction.OnToggleThemeDropdown)
                                    },
                                    onOptionSelected = {
                                        onAction(SettingsAction.OnThemeSelected(it))
                                    },
                                    containerWidth = containerWidth
                                )
                            }
                        }
                    }

                    @Composable
                    private fun ThemeDropdown(
                        isExpanded: Boolean,
                        selectedTheme: NewYearTheme,
                        onDismissRequest: () -> Unit,
                        onOptionSelected: (NewYearTheme) -> Unit,
                        modifier: Modifier = Modifier,
                        containerWidth: Dp
                    ) {
                        DropdownMenu(
                            expanded = isExpanded,
                            onDismissRequest = onDismissRequest,
                            modifier = modifier.width(containerWidth),
                            offset = DpOffset(x = 0.dp, y = 8.dp),
                            containerColor = SettingsColors.surfaceInput,
                            border = BorderStroke(
                                width = 1.dp, color = SettingsColors.outlineInput
                            ),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            NewYearTheme.entries.forEach { theme ->
                                DropdownMenuItem(
                                    text = {
                                        Text(
                                            text = theme.label(),
                                            fontFamily = plusJakartaSansFont(),
                                            fontWeight = FontWeight.Medium,
                                            fontSize = 18.sp,
                                            color = SettingsColors.textPrimary
                                        )
                                    },
                                    trailingIcon = {
                                        if (selectedTheme == theme) {
                                            Icon(
                                                imageVector = Icons.Default.Done,
                                                contentDescription = null,
                                                modifier = Modifier.size(16.dp),
                                                tint = SettingsColors.primary
                                            )
                                        }
                                    },
                                    onClick = {
                                        onOptionSelected(theme)
                                    },
                                    contentPadding = PaddingValues(
                                        horizontal = 8.dp, vertical = 10.dp
                                    )
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "IntrinsicSize",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "IntrinsicSize.Min: Make this layout as small as its smallest child needs it to be. Useful when you want a Row or Column to shrink to fit its shortest child.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "IntrinsicSize.Max: Make this layout as big as its largest child needs it to be. Useful when you want your layout to always match the tallest (or widest) child.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "defaultMinSize",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //TextField Height 
                    
                    Modifier
                        .border(
                            width = 1.dp,
                            color = currentBorderColor,
                            shape = RoundedCornerShape(16.dp),
                        )
                        .clip(shape = RoundedCornerShape(16.dp))
                        .clipToBounds()
                        .background(AppTheme.color.surfaceHigh, shape = RoundedCornerShape(16.dp))
                        .defaultMinSize(minHeight = 56.dp)
                    """.trimIndent()
            )

            SpanText(
                text = "Modifier.trackVisibility",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine you have a LazyColumn of video items, and you only want to play the video that’s currently visible on the screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun VideoItem(playerManager: ExoPlayerManager, index: Int) {
                         val singleController: ExoPlayerController = remember {
                            playerManager.createPlayerController(UUID.randomUUID().toString())
                        }

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(200.dp)
                                .trackVisibility(thresholdPercentage = 0.7f) { visibilityInfo ->
                                    if (visibilityInfo.isAboveThreshold) {
                                        singleController.play()
                                    } else {
                                        singleController.pause()
                                    }
                                }
                        ) {
                            VideoPlayer(
                                controller = singleController,
                                mediaSource = MediaSource.Network("https://ireplay.tv/test/blender.m3u8"),
                                contentScale = ContentScale.Crop,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Modifier.semantics",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "contentDescription to UI elements. This helps screenReader accurately identify and announce the purpose of each element. Semantics in Jetpack Compose are like extra labels or tags that you attach to your UI elements (Button , Text , TextField etc) . They tell both accessibility tools (like screen readers) and testing tools what each part of your app is for. Semantics are useful to test the UI components as well as to provide the information about the UI components to the disabled person ( that using your app).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun SettingsItem(
                        title: String,
                        isChecked: Boolean,
                        onCheckedChange: (Boolean) -> Unit
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onCheckedChange(!isChecked) }
                                .semantics {
                                    stateDescription = if (isChecked) "Enabled" else "Disabled"
                                }
                                .padding(16.dp)
                        ) {
                            Text(
                                text = title,
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.weight(1f)
                            )

                            Checkbox(
                                checked = isChecked,
                                onCheckedChange = onCheckedChange
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Merging Semantics for Grouped Content",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can merge multiple elements into a single accessible node using the mergeDescendants property inside Modifier.semantics.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When multiple elements should be read together (e.g., title and subtitle).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When separate elements don’t provide meaning alone.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    Column(
                        modifier = Modifier.semantics { mergeDescendants = true }
                    ) {
                        Text(text = "Kotlin", style = MaterialTheme.typography.bodyLarge)
                        Text(text = "July 22, 2011", style = MaterialTheme.typography.bodyMedium)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "onGloballyPositioned Mental Model: When Does It Fire?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    var width by remember { mutableStateOf(0) }
                    
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .onGloballyPositioned { width = it.size.width }
                    ) {
                        // This will recompose when width changes!
                        Text("Width: $width px")
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "onGloballyPositioned fires during the Layout phase, but the lambda captures values that can trigger Recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Initial composition → Layout phase → onGloballyPositioned fires → width updates.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State change triggers recomposition → Layout phase → onGloballyPositioned fires again.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If width is the same, no more recomposition (smart equality check).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://medium.com/@sehajkahlon437
            KotlinCode(
                code = """
                    // ❌ Don't do this - you already know the size!
                    Box(
                        modifier = Modifier
                            .size(100.dp)
                            .onGloballyPositioned { 
                                size = it.size // Pointless - you set it to 100.dp!
                            }
                    )
                    
                    // ✅ Only use when size is dynamic
                    Box(
                        modifier = Modifier
                            .fillMaxWidth() // Dynamic - depends on parent
                            .onGloballyPositioned { width = it.size.width }
                    )
                    
                    //Heavy Operations in the Callback
                    // ❌ Don't do expensive work here
                    .onGloballyPositioned { coordinates ->
                        // This runs on the main thread during layout!
                        saveToDisk(coordinates.size) 
                        analyticsSdk.track("layout_complete", coordinates)
                    }
                    
                    // ✅ Just capture the values, process later
                    .onGloballyPositioned { coordinates ->
                        heightPx = coordinates.size.height.toFloat()
                    }
                    LaunchedEffect(heightPx) {
                        // Heavy work in a coroutine
                        analyticsSdk.track("layout_complete", heightPx)
                    }
                    
                    //Forgetting About Density
                    // ❌ Mixing pixels and dp incorrectly
                    .onGloballyPositioned { coordinates ->
                        val heightPx = coordinates.size.height
                        spacerHeight = heightPx.dp // WRONG! This treats pixels as dp
                    }
                    
                    // ✅ Convert properly using density
                    val density = LocalDensity.current
                    .onGloballyPositioned { coordinates ->
                        val heightPx = coordinates.size.height
                        spacerHeight = with(density) { heightPx.toDp() }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Weight Vs Fraction",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Weight is a relative measurement that only works within specific parent containers (Row, Column, or RowScope/ColumnScope). It distributes available space among siblings based on their weight ratios.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Fraction is an absolute measurement that calculates size as a percentage of the parent's dimension, regardless of siblings or container type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    private fun PetDetailView(
                        modifier: Modifier = Modifier
                    ) {
                        Row(
                            modifier = modifier
                                .padding(horizontal = 30.dp, vertical = 10.dp)
                                .clip(RoundedCornerShape(4.dp))
                                .border(width = 1.dp, color = MaterialTheme.colorScheme.surface)
                                .fillMaxSize()
                        ) {
                            Column(
                                modifier = Modifier.weight(0.5f).padding(10.dp)
                            ) { }
                            
                            LazyColumn(
                                modifier = Modifier.weight(0.5f).padding(10.dp)
                            ) { }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun InfoCard(
                        description: String,
                        modifier: Modifier = Modifier,
                        onClosedClick: () -> Unit = {},
                    ) {
                        Row(
                            modifier = modifier
                                .clip(RoundedCornerShape(Theme.radius.s))
                                .background(Theme.color.background.card)
                                .border(1.dp, Theme.color.stroke.primary, RoundedCornerShape(Theme.radius.s))
                                .padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                painter = painterResource(Theme.icons.dueTone.infoCircle),
                                contentDescription = null,
                                tint = Theme.color.brand.primary,
                                modifier = Modifier
                                    .size(20.dp)
                            )
                            Text(
                                text = description,
                                style = Theme.textStyle.body.sm.regular,
                                color = Theme.color.shade.primary,
                                modifier = Modifier
                                    .padding(horizontal = 8.dp)
                                    .weight(1f),
                                maxLines = 2,
                                overflow = TextOverflow.Ellipsis
                            )
                            Icon(
                                painter = painterResource(Theme.icons.outline.close),
                                contentDescription = null,
                                tint = Theme.color.shade.secondary,
                                modifier = Modifier
                                    .size(16.dp)
                                    .clickable(onClick = onClosedClick)
                            )

                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "measuredHeight - Icon Alignment Center",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Icon(
                            painter = painterResource(R.drawable.arrow),
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onPrimary,
                            modifier = Modifier.alignBy { it.measuredHeight })

                        Text(
                            text = it,
                            style = MaterialTheme.typography.labelLarge,
                            modifier = Modifier.alignBy(FirstBaseline)
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn Animation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/enmanuel52/Pop-Corns.git
            KotlinCode(
                code = """
                    @Composable
                    fun LazyGridState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex >= firstVisibleItemIndex
                                } else {
                                    previousScrollOffset >= firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }

                    @Composable
                    fun LazyListState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex > firstVisibleItemIndex
                                } else {
                                    previousScrollOffset > firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }
                    
                    /**
                     * @param rotation in degrees
                     * */
                    fun Modifier.listItemWindAnimation(
                        isScrollingForward: Boolean,
                        orientation: Orientation = Orientation.Vertical,
                        rotation: Float = 15f,
                        durationMillis: Int = 400,
                    ): Modifier = composed {
                        val cameraAnimatable = remember { Animatable(initialValue = 7.0f) }
                        val scaleAnimatable = remember { Animatable(initialValue = 0.8f) }
                        val rotateAnimatable = remember(isScrollingForward) {
                            Animatable(initialValue = if (isScrollingForward) rotation else -rotation)
                        }

                        // Observe changes to scrollDirection and update rotateAnimatable accordingly
                        LaunchedEffect(isScrollingForward) {
                            // Animate from 0 to either 60 or -60
                            rotateAnimatable.animateTo(
                                if (isScrollingForward) rotation else -rotation,
                                animationSpec = tween(
                                    durationMillis = 100,
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                            // Animate from either 60 or -60 to 0
                            rotateAnimatable.animateTo(
                                targetValue = 0f,
                                animationSpec = tween(
                                    durationMillis = durationMillis,
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                        }

                        // Other animations (camera and scale) remain the same
                        LaunchedEffect(Unit) {
                            cameraAnimatable.animateTo(
                                8.0f,
                                animationSpec = tween(
                                    durationMillis = durationMillis.times(1.4f).roundToInt(),
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                        }

                        LaunchedEffect(Unit) {
                            scaleAnimatable.animateTo(
                                1f,
                                animationSpec = tween(
                                    durationMillis = durationMillis.times(1.4f).roundToInt(),
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                        }

                        graphicsLayer {
                            scaleX = scaleAnimatable.value
                            scaleY = scaleAnimatable.value
                            cameraDistance = cameraAnimatable.value
                            when (orientation) {
                                Orientation.Vertical -> {
                                    rotationX = -rotateAnimatable.value
                                }

                                Orientation.Horizontal -> {
                                    rotationY = rotateAnimatable.value
                                }
                            }
                        }
                    }
                    
                    //UI (LazyVerticalGrid)
                    Modifier
                        .widthIn(max = 200.dp)
                        .listItemWindAnimation(
                            isScrollingForward = listState.isScrollingForward(),
                            rotation = 4f,
                            durationMillis = 200
                        )
                        
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/proandroiddev/compose-drawing-mastery-part-1-the-drawscope-foundation-06c8935e54a0